import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import { Box } from "@mui/material";
import { GridSortItem } from "@mui/x-data-grid";
import { FormikProps } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import CustomDataGridV2 from "../../../../../components/CustomDataGridV2";
import CustomSelectScroll from "../../../../../components/CustomSelectScroll";
import { Option } from "../../../../../constants/common";
import Text from "../../../../../components/Text";
import DeleteIcon from "../../../../../components/app/DeleteIcon";
import DeleteModal from "../../../../../components/app/DeleteModal";
import { AddSuperBillForm } from "../../../../../constants/provider/super-bill/add-super-bill/add-super-bill";
import useDisclosure from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import useGetCataLogCode from "../../../../../queries/catalog/use-get-catalog-code";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
} from "../../../../../utils/utils";
import CardView from "../../patient/patient-list/patient-chart-new/assessment/summary/components/CardView";
import { MODIFIER_KEYS } from "../common-components/constant";
interface DiagnosisCodeGridProps {
  formik: FormikProps<AddSuperBillForm>;
  viewBill: boolean;
  diagnosisCodes?: any;
  isFetching: boolean;
  isLoading: boolean;
  selectedPointers: any;
  // A Patient Responsibility invoice carries the diagnosis codes the claim was
  // adjudicated on, so the list can only be read - never added to or trimmed.
  isPatientResponsibilityInvoice?: boolean;
}

const DiagnosisCodeGrid = ({
  formik,
  viewBill = true,
  diagnosisCodes,
  isFetching,
  isLoading,
  selectedPointers,
  isPatientResponsibilityInvoice = false,
}: DiagnosisCodeGridProps) => {
  const canEditCodes = viewBill && !isPatientResponsibilityInvoice;
  const [gridSorting] = useState<GridSortItem[]>([]);
  const [tableDataLoading, setTableDataLoading] = useState<boolean>(false);
  const [gridData, setGridData] = useState<any>([]);

  const resArrayToCheckMod = MODIFIER_KEYS?.map(
    (k) => !!selectedPointers?.some((obj: any) => !!obj[k])
  );

  const showSnackbar = useSnackbar();
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [searchKey, setSearchKey] = useState<string>("");
  const [input, setInput] = useState<any>("");
  const confirmDeleteDisc = useDisclosure();

  const { data, isFetching: codeFetching } = useGetCataLogCode({
    gridSorting,
    pagination,
    searchKey,
    type: "ICD",
    active: true,
    archive: false,
  });

  // Keep every catalog item seen across pages so a code picked after scrolling
  // can still be resolved to its full record when added to the grid.
  const codeByUuid = useRef<Record<string, any>>({});
  useEffect(() => {
    (data?.data?.content ?? []).forEach((it: any) => {
      if (it?.uuid) codeByUuid.current[it.uuid] = it;
    });
  }, [data?.data?.content]);

  // Calculate dynamic height based on grid data count
  const dynamicHeight = useMemo(() => {
    const rowHeight = 35; // Height of each row
    const headerHeight = 56; // Approximate header height
    const minHeight = 100; // Minimum height
    const maxHeight = 400; // Maximum height to prevent excessive growth

    const dataCount = gridData?.length || 0;
    const calculatedHeight = headerHeight + dataCount * rowHeight;

    // Return calculated height within bounds
    return Math.max(minHeight, Math.min(calculatedHeight, maxHeight));
  }, [gridData]);

  const COLUMNS = applyDefaultColumnSettings<any>([
    {
      field: "pointer",
      flex: 0.08,
      disableColumnMenu: true,
      editable: false,
      minWidth: 40,
      headerName: "Pointer",
      renderCell: (params) => {
        const index = params.api.getAllRowIds().indexOf(params.id);
        return `${String.fromCharCode(65 + index)}.`;
      },
    },
    {
      field: "code",
      flex: 0.1,
      minWidth: 3,
      disableColumnMenu: true,
      headerName: "Code",
      renderCell: ({ row }) => {
        return <Text>{row?.code}</Text>;
      },
    },
    {
      field: "description",
      flex: 0.5,
      disableColumnMenu: true,
      headerName: "Details",
    },
    {
      field: "SelectFields",
      flex: 0.15,
      align: "center",
      disableColumnMenu: true,
      // The "Action" header carried no meaning and sat centred while the icon
      // renders right-aligned, so the label is dropped.
      headerName: "",
      renderCell: (params) => {
        const rowIndex = params.api.getAllRowIds().indexOf(params.id);

        return (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "row-reverse",
            }}
          >
            <DeleteIcon
              disabled={!canEditCodes}
              onClick={() => {
                if (resArrayToCheckMod[rowIndex]) {
                  confirmDeleteDisc.open();
                  return;
                }
                setTableDataLoading(true);
                // console.log("button clicked", uuid);
                setGridData((prevItems: any) =>
                  prevItems.filter(
                    (item: any) => item.uuid !== params?.row?.uuid
                  )
                );
                setTableDataLoading(false);
              }}
            />
          </Box>
        );
      },
    },
  ]);

  useEffect(() => {
    formik.setFieldValue("diagnosisCodes", gridData);
    formik.setFieldValue("diagnisisCodeData", gridData);
  }, [gridData]);

  useEffect(() => {
    if (diagnosisCodes) {
      setGridData(diagnosisCodes);
    }
  }, [diagnosisCodes]);

  return (
    <Box id={"Provider Details"}>
      <CardView
        title="Diagnosis Code"
        titleIcon={
          <ListAltOutlinedIcon sx={{ color: "blue" }} fontSize="small" />
        }
        endAction={
          canEditCodes && (
            <Box sx={{ paddingX: "5px" }} width={"30.5vw"}>
              <CustomSelectScroll
                key={`diag-select-${gridData?.length ?? 0}`}
                disablePortal={false}
                value={input}
                placeholder="Search & select ICD Code"
                loading={codeFetching}
                pagination={pagination}
                setPagination={setPagination}
                totalElements={data?.data?.page?.totalElements}
                onSearch={(_, val) => {
                  setSearchKey(val);
                  setPagination((p) => ({ ...p, page: 0 }));
                }}
                options={data?.data?.content?.reduce((acc: Option[], it) => {
                  if (!gridData?.some((item: any) => item?.uuid === it?.uuid)) {
                    acc.push({
                      label: `${it?.code ?? ""} - ${it?.description ?? ""}`,
                      value: it?.uuid,
                    });
                  }

                  return acc;
                }, [])}
                onChange={(v: any) => {
                  setTableDataLoading(true);
                  if (gridData?.length >= 12) {
                    showSnackbar({
                      message: "Max 12 diagnosis codes allowed",
                      type: "error",
                    });
                    setTableDataLoading(false);
                    return;
                  }

                  const curOption =
                    codeByUuid.current[v] ??
                    data?.data?.content?.find((it) => it?.uuid === v);
                  setInput(v);
                  if (curOption) {
                    if (!gridData?.some((item: any) => item?.uuid === v)) {
                      // Clear the input after successful addition
                      setTimeout(() => setInput(""), 0);
                      setGridData((prevItems: any) => [
                        ...prevItems,
                        curOption,
                      ]);
                      setPagination(DEF_PAGINATION);
                    } else {
                      showSnackbar({
                        message: "Duplicate Entry",
                        type: "error",
                      });
                    }
                  }
                  setTableDataLoading(false);
                }}
              />
            </Box>
          )
        }
      >
        <DeleteModal
          w="380px"
          cancelButtonText="Cancel"
          confirmButtonText="Okay"
          open={confirmDeleteDisc.opened}
          onClose={confirmDeleteDisc.close}
          disableOnSaveSnackbar
          onConfirmation={() => Promise.resolve(true)}
          content={
            "You have selected a diagnosis pointer for Below Procedure Code"
          }
        />
        <Box height={"100%"}>
          {/* <Text
          fontWeight={"bold"}
          variant="h6"
          fontSize={"14px"}
          color={SECONDARY_TEXT_COLOR}
          alignSelf={"left"}
          margin={"5px 0px"}
        >
          ICD Code
        </Text> */}
          <Box height={"100%"}>
            <CustomDataGridV2
              // searchString={""}
              height={`${dynamicHeight}px`}
              loading={tableDataLoading || isLoading || isFetching}
              getRowId={(row) => row?.uuid}
              rows={gridData ?? []}
              columns={COLUMNS}
              hideFooter
              rowHeight={35}
            />
          </Box>
        </Box>
      </CardView>
    </Box>
  );
};

export default DiagnosisCodeGrid;
